import { useEffect } from 'react';
import { usePusherStore } from '../store';

/**
 * Subscribes to the conversation's Pusher presence channel and invokes onMessageProcessed
 * whenever travel-service emits MESSAGE_PROCESSED for that conversation. The push is fired
 * by the async worker after all create-message side effects (sales stage, reminders, message
 * state) have been applied, so consumers should call SWR mutate from the callback to refresh
 * any cache that depends on those side effects.
 *
 * Usage notes:
 *   - DO NOT mount this hook on a screen where useZicassoChat is also mounted with
 *     subscribeToRealtime: true on the same conversation. Both hooks unbind by event name
 *     (not by callback ref), so co-mounting would cause one to clobber the other's listener.
 *   - Safe to use anywhere useZicassoChat is not mounted (e.g. trip-match detail page).
 */
const useMessageProcessedListener = (
  conversationId: string | undefined,
  onMessageProcessed: () => void,
) => {
  const {
    bindToEvent,
    ready,
    subscribeToChannel,
    subscribedChannels,
    unbindFromEvent,
    unsubscribeFromChannel,
  } = usePusherStore();

  const channelName = conversationId ? `presence-${conversationId}` : '';

  useEffect(() => {
    if (!ready || !channelName) return undefined;
    subscribeToChannel(channelName);

    return () => {
      if (ready) {
        unsubscribeFromChannel(channelName);
      }
    };
  }, [ready, channelName]);

  useEffect(() => {
    if (!channelName || !subscribedChannels[channelName]) return undefined;

    bindToEvent(channelName, 'MESSAGE_PROCESSED', () => {
      onMessageProcessed();
    });

    return () => {
      if (subscribedChannels[channelName]) {
        unbindFromEvent(channelName, 'MESSAGE_PROCESSED');
      }
    };
  }, [subscribedChannels, channelName, onMessageProcessed]);
};

export default useMessageProcessedListener;
